/**
 * Audit Log Detail Panel
 *
 * Expandable detail view for a single audit log record, shown inline
 * within the DataTable expanded row.
 *
 * @module superadmin/AuditLogDetailPanel
 * @see docs/action-audit-log.md
 */

import React from 'react';
import { Flex, Box, Text, TagBadge, badgeVariantToTagColor } from '@chatsdet/ui';
import type { AuditLogRecord } from './useAuditLogData';
import { CopyablePre } from '../../components/CopyButton';

function CodeBlock({ children, label }: { children: string; label?: string }) {
  return (
    <CopyablePre value={children} label={label}>
      <pre className="bg-muted rounded-md p-3 text-xs font-mono overflow-x-auto whitespace-pre-wrap break-all max-h-[200px] overflow-y-auto">
        {children}
      </pre>
    </CopyablePre>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box className="space-y-1">
      <Text size="xs" weight="medium" className="text-muted-foreground uppercase tracking-wider">
        {label}
      </Text>
      {children}
    </Box>
  );
}

export function AuditLogDetailPanel({ record }: { record: AuditLogRecord }) {
  const hasAgentContext =
    record.agentConditionReasoning ||
    record.agentAssumptions?.length ||
    record.agentFailureContext ||
    record.agentSuggestedInstructions;

  return (
    <Box className="p-4 bg-muted/30 border-t border-border space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left column: Action details */}
        <Flex direction="column" gap="md">
          {record.elementDescription && (
            <Section label="Element">
              <Text size="sm">{record.elementDescription}</Text>
            </Section>
          )}

          {record.elementSelectors !== null && record.elementSelectors !== undefined && (
            <Section label="Selectors">
              <CodeBlock>{JSON.stringify(record.elementSelectors, null, 2)}</CodeBlock>
            </Section>
          )}

          {record.actionArgs !== null && record.actionArgs !== undefined && (
            <Section label="Action Args">
              <CodeBlock>{JSON.stringify(record.actionArgs, null, 2)}</CodeBlock>
            </Section>
          )}

          {(record.httpMethod || record.requestUrl) && (
            <Section label="HTTP Details">
              <Flex gap="sm" align="center" wrap>
                {record.httpMethod && <TagBadge name={record.httpMethod} color="gray" hideDot />}
                {record.requestUrl && (
                  <Text size="sm" className="font-mono break-all">
                    {record.requestUrl}
                  </Text>
                )}
                {record.responseStatusCode !== null && record.responseStatusCode !== undefined && (
                  <TagBadge name={record.responseStatusCode} color={badgeVariantToTagColor(record.responseStatusCode < 400 ? 'success' : 'destructive')} hideDot />
                )}
              </Flex>
            </Section>
          )}

          {record.toolName && (
            <Section label="Tool">
              <Text size="sm" className="font-mono">
                {record.toolName}
              </Text>
            </Section>
          )}
        </Flex>

        {/* Right column: Agent context */}
        <Flex direction="column" gap="md">
          {record.agentConditionReasoning && (
            <Section label="Condition Reasoning">
              <Text size="sm">{record.agentConditionReasoning}</Text>
            </Section>
          )}

          {record.agentAssumptions && record.agentAssumptions.length > 0 && (
            <Section label="Assumptions">
              <ul className="list-disc list-inside space-y-0.5">
                {record.agentAssumptions.map((a, i) => (
                  <li key={i}>
                    <Text size="sm" as="span">
                      {a}
                    </Text>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {record.agentFailureContext && (
            <Section label="Failure Context">
              <Box className="bg-destructive/10 border border-destructive/20 rounded-md p-3 space-y-2">
                {record.agentFailureContext.observedBehavior && (
                  <div>
                    <Text size="xs" weight="medium">
                      Observed:
                    </Text>
                    <Text size="sm">{record.agentFailureContext.observedBehavior}</Text>
                  </div>
                )}
                {record.agentFailureContext.expectedBehavior && (
                  <div>
                    <Text size="xs" weight="medium">
                      Expected:
                    </Text>
                    <Text size="sm">{record.agentFailureContext.expectedBehavior}</Text>
                  </div>
                )}
                {record.agentFailureContext.reproSteps?.length ? (
                  <div>
                    <Text size="xs" weight="medium">
                      Repro Steps:
                    </Text>
                    <ol className="list-decimal list-inside">
                      {record.agentFailureContext.reproSteps.map((s, i) => (
                        <li key={i}>
                          <Text size="sm" as="span">
                            {s}
                          </Text>
                        </li>
                      ))}
                    </ol>
                  </div>
                ) : null}
                {record.agentFailureContext.questions?.length ? (
                  <div>
                    <Text size="xs" weight="medium">
                      Questions:
                    </Text>
                    <ul className="list-disc list-inside">
                      {record.agentFailureContext.questions.map((q, i) => (
                        <li key={i}>
                          <Text size="sm" as="span">
                            {q}
                          </Text>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </Box>
            </Section>
          )}

          {record.agentSuggestedInstructions && (
            <Section label="Suggested Instructions">
              <Text size="sm">{record.agentSuggestedInstructions}</Text>
            </Section>
          )}

          {!hasAgentContext && (
            <Text size="sm" className="text-muted-foreground italic">
              No agent annotations for this action.
            </Text>
          )}
        </Flex>
      </div>

      {/* Bottom: Error + Metadata */}
      {record.errorMessage && (
        <Box className="bg-destructive/10 border border-destructive/20 rounded-md p-3">
          <Text size="xs" weight="medium" className="text-destructive mb-1">
            Error
          </Text>
          <Text size="sm" className="font-mono break-all">
            {record.errorMessage}
          </Text>
        </Box>
      )}

      <Flex gap="lg" wrap className="text-xs text-muted-foreground">
        {record.screenshotS3Key && (
          <span>
            Screenshot: <span className="font-mono">{record.screenshotS3Key}</span>
          </span>
        )}
        <span>
          Session: <span className="font-mono">{record.sessionId}</span>
        </span>
        {record.runStepId && (
          <span>
            Step: <span className="font-mono">{record.runStepId}</span>
          </span>
        )}
        {record.nodeId && (
          <span>
            Node: <span className="font-mono">{record.nodeId}</span>
          </span>
        )}
        {record.credentialId && (
          <span>
            Credential: <span className="font-mono">{record.credentialId}</span>
          </span>
        )}
      </Flex>
    </Box>
  );
}
